import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  TrendingUp as PerformanceIcon,
  EmojiEvents as GoalsIcon,
  Assessment as ReviewIcon,
  School as DevelopmentIcon,
  Feedback as FeedbackIcon,
  Star as RatingIcon,
  CheckCircle as CompletedIcon,
  Schedule as InProgressIcon,
  Warning as OverdueIcon,
  Add as AddIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

interface Goal {
  id: number;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  progress_percentage: number;
  target_date: string;
  weight_percentage: number;
}

interface PerformanceReview {
  id: number;
  review_type: string;
  status: 'draft' | 'pending' | 'in_progress' | 'completed' | 'approved';
  overall_rating?: string;
  overall_score?: number;
  review_period_start: string;
  review_period_end: string;
  due_date: string;
}

interface FeedbackEntry {
  id: number;
  feedback_giver_name: string;
  feedback_type: string;
  feedback_category: string;
  rating?: number;
  created_at: string;
}

interface DevelopmentPlan {
  id: number;
  plan_name: string;
  description: string;
  progress_percentage: number;
  status: string;
  target_completion_date: string;
}

const PerformanceDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [reviews, setReviews] = useState<PerformanceReview[]>([]);
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [developmentPlans, setDevelopmentPlans] = useState<DevelopmentPlan[]>([]);
  const [performanceSummary, setPerformanceSummary] = useState<any>(null);

  useEffect(() => {
    fetchPerformanceData();
  }, []);

  const fetchPerformanceData = async () => {
    // Mock data for demonstration
    setGoals([
      {
        id: 1,
        title: 'Increase Sales Revenue by 15%',
        description: 'Focus on new client acquisition and upselling existing accounts',
        category: 'performance',
        priority: 'high',
        status: 'in_progress',
        progress_percentage: 75,
        target_date: '2024-03-31',
        weight_percentage: 30
      },
      {
        id: 2,
        title: 'Complete React Certification',
        description: 'Obtain React Developer certification to enhance technical skills',
        category: 'development',
        priority: 'medium',
        status: 'in_progress',
        progress_percentage: 45,
        target_date: '2024-02-28',
        weight_percentage: 20
      },
      {
        id: 3,
        title: 'Improve Team Collaboration',
        description: 'Lead cross-functional projects and mentor junior team members',
        category: 'behavioral',
        priority: 'high',
        status: 'completed',
        progress_percentage: 100,
        target_date: '2024-01-31',
        weight_percentage: 25
      }
    ]);

    setReviews([
      {
        id: 1,
        review_type: 'Annual Review',
        status: 'completed',
        overall_rating: 'exceeds_expectations',
        overall_score: 4.2,
        review_period_start: '2023-01-01',
        review_period_end: '2023-12-31',
        due_date: '2024-01-15'
      },
      {
        id: 2,
        review_type: 'Quarterly Review',
        status: 'pending',
        review_period_start: '2024-01-01',
        review_period_end: '2024-03-31',
        due_date: '2024-04-15'
      }
    ]);

    setFeedback([
      {
        id: 1,
        feedback_giver_name: 'Sarah Manager',
        feedback_type: 'manager',
        feedback_category: 'performance',
        rating: 4,
        created_at: '2024-01-10T10:00:00'
      },
      {
        id: 2,
        feedback_giver_name: 'John Colleague',
        feedback_type: 'peer',
        feedback_category: 'teamwork',
        rating: 5,
        created_at: '2024-01-08T14:30:00'
      },
      {
        id: 3,
        feedback_giver_name: 'Emma Team Lead',
        feedback_type: 'manager',
        feedback_category: 'leadership',
        rating: 4,
        created_at: '2024-01-05T09:15:00'
      }
    ]);

    setDevelopmentPlans([
      {
        id: 1,
        plan_name: 'Leadership Development Program',
        description: 'Comprehensive leadership skills development',
        progress_percentage: 60,
        status: 'active',
        target_completion_date: '2024-06-30'
      },
      {
        id: 2,
        plan_name: 'Technical Skills Enhancement',
        description: 'Advanced React and TypeScript training',
        progress_percentage: 30,
        status: 'active',
        target_completion_date: '2024-04-30'
      }
    ]);

    setPerformanceSummary({
      total_goals: 3,
      completed_goals: 1,
      goal_completion_rate: 33.3,
      average_goal_progress: 73.3,
      total_reviews: 2,
      latest_review_rating: 'exceeds_expectations',
      total_feedback_entries: 3,
      average_feedback_rating: 4.3,
      active_development_plans: 2
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'overdue': return 'error';
      case 'not_started': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CompletedIcon />;
      case 'in_progress': return <InProgressIcon />;
      case 'overdue': return <OverdueIcon />;
      default: return <InProgressIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getRatingStars = (rating?: number) => {
    if (!rating) return 'N/A';
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Performance Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track goals, reviews, feedback, and career development
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button startIcon={<AddIcon />} variant="outlined">
            New Goal
          </Button>
          <Button startIcon={<FeedbackIcon />} variant="contained">
            Request Feedback
          </Button>
        </Box>
      </Box>

      {/* Performance Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <GoalsIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="primary">
                {performanceSummary?.goal_completion_rate.toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Goal Completion Rate
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {performanceSummary?.completed_goals}/{performanceSummary?.total_goals} goals completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <RatingIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {performanceSummary?.average_feedback_rating.toFixed(1)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Rating
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Based on {performanceSummary?.total_feedback_entries} feedback entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ReviewIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {performanceSummary?.latest_review_rating?.replace('_', ' ').toUpperCase() || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Latest Review
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {performanceSummary?.total_reviews} reviews completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <DevelopmentIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {performanceSummary?.active_development_plans}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Dev Plans
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Career development in progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Alert */}
      <Alert severity="info" sx={{ mb: 4 }}>
        <Typography variant="subtitle2" gutterBottom>
          🎯 Performance Review Period Active
        </Typography>
        <Typography variant="body2">
          Your quarterly review is due on April 15th. Complete your self-assessment and gather feedback from colleagues to prepare.
        </Typography>
      </Alert>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label="Goals & Objectives" />
          <Tab label="Performance Reviews" />
          <Tab label="360° Feedback" />
          <Tab label="Development Plans" />
        </Tabs>
      </Box>

      {/* Goals Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Grid container spacing={3}>
          {goals.map((goal) => (
            <Grid item xs={12} md={6} key={goal.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {goal.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {goal.description}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                      <Chip
                        label={goal.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(goal.status) as any}
                        size="small"
                        icon={getStatusIcon(goal.status)}
                      />
                      <Chip
                        label={goal.priority.toUpperCase()}
                        color={getPriorityColor(goal.priority) as any}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Progress</Typography>
                      <Typography variant="body2">{goal.progress_percentage}%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={goal.progress_percentage} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Due: {formatDate(goal.target_date)} • Weight: {goal.weight_percentage}%
                    </Typography>
                    <Button size="small" startIcon={<ViewIcon />}>
                      View Details
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Reviews Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Grid container spacing={3}>
          {reviews.map((review) => (
            <Grid item xs={12} md={6} key={review.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        {review.review_type}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Period: {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}
                      </Typography>
                    </Box>
                    <Chip
                      label={review.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(review.status) as any}
                      size="small"
                    />
                  </Box>
                  
                  {review.overall_rating && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Overall Rating: {review.overall_rating.replace('_', ' ').toUpperCase()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h4" color="primary">
                          {review.overall_score?.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          / 5.0
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Due: {formatDate(review.due_date)}
                    </Typography>
                    <Button size="small" startIcon={<ViewIcon />}>
                      {review.status === 'pending' ? 'Complete Review' : 'View Review'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Feedback Tab */}
      <TabPanel value={selectedTab} index={2}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Recent Feedback</Typography>
            <List>
              {feedback.map((fb, index) => (
                <React.Fragment key={fb.id}>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {fb.feedback_giver_name.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2">
                            {fb.feedback_giver_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={fb.feedback_type.toUpperCase()}
                              size="small"
                              variant="outlined"
                            />
                            <Typography variant="body2" color="primary">
                              {getRatingStars(fb.rating)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Category: {fb.feedback_category} • {formatDate(fb.created_at)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < feedback.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Development Plans Tab */}
      <TabPanel value={selectedTab} index={3}>
        <Grid container spacing={3}>
          {developmentPlans.map((plan) => (
            <Grid item xs={12} md={6} key={plan.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {plan.plan_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {plan.description}
                      </Typography>
                    </Box>
                    <Chip
                      label={plan.status.toUpperCase()}
                      color={getStatusColor(plan.status) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Progress</Typography>
                      <Typography variant="body2">{plan.progress_percentage}%</Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={plan.progress_percentage} 
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Target: {formatDate(plan.target_completion_date)}
                    </Typography>
                    <Button size="small" startIcon={<ViewIcon />}>
                      View Plan
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default PerformanceDashboard;
